type CameraFacingMode = 'user' | 'environment';

type AcquireCameraSessionOptions = {
  facingMode?: CameraFacingMode;
  widthIdeal?: number;
  heightIdeal?: number;
  frameRateIdeal?: number;
};

type ReleaseCameraSessionOptions = {
  immediate?: boolean;
};

type ActiveSession = {
  stream: MediaStream;
  facingMode: CameraFacingMode;
  refs: number;
  releaseTimer: number | null;
};

let activeSession: ActiveSession | null = null;

function clearReleaseTimer() {
  if (activeSession?.releaseTimer != null) {
    window.clearTimeout(activeSession.releaseTimer);
    activeSession.releaseTimer = null;
  }
}

function stopActiveStreamNow() {
  if (!activeSession) return;
  clearReleaseTimer();
  for (const track of activeSession.stream.getTracks()) {
    track.stop();
  }
  activeSession = null;
}

function isStreamLive(stream: MediaStream): boolean {
  const tracks = stream.getVideoTracks();
  if (!tracks.length) return false;
  return tracks.some((track) => track.readyState === 'live');
}

function startDeferredRelease() {
  if (!activeSession) return;
  clearReleaseTimer();
  activeSession.releaseTimer = window.setTimeout(() => {
    stopActiveStreamNow();
  }, 30000);
}

export async function acquireCameraSession(
  options: AcquireCameraSessionOptions = {},
): Promise<{ stream: MediaStream; release: (options?: ReleaseCameraSessionOptions) => void }> {
  const facingMode = options.facingMode ?? 'environment';
  const widthIdeal = options.widthIdeal ?? 1280;
  const heightIdeal = options.heightIdeal ?? 720;
  const frameRateIdeal = options.frameRateIdeal;

  if (activeSession && isStreamLive(activeSession.stream) && activeSession.facingMode === facingMode) {
    clearReleaseTimer();
    activeSession.refs += 1;
    const acquiredStream = activeSession.stream;
    let released = false;
    return {
      stream: acquiredStream,
      release: (releaseOptions = {}) => {
        if (released) return;
        released = true;
        if (!activeSession || activeSession.stream !== acquiredStream) return;
        activeSession.refs = Math.max(0, activeSession.refs - 1);
        if (activeSession.refs === 0) {
          if (releaseOptions.immediate) stopActiveStreamNow();
          else startDeferredRelease();
        }
      },
    };
  }

  if (activeSession) {
    stopActiveStreamNow();
  }

  if (!navigator.mediaDevices?.getUserMedia) {
    throw new Error('Camera access not supported on this device.');
  }
  if (!window.isSecureContext) {
    throw new Error('Camera needs HTTPS (or localhost).');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    video: {
      facingMode,
      width: { ideal: widthIdeal },
      height: { ideal: heightIdeal },
      ...(frameRateIdeal ? { frameRate: { ideal: frameRateIdeal } } : {}),
    },
  });

  activeSession = {
    stream,
    facingMode,
    refs: 1,
    releaseTimer: null,
  };

  let released = false;
  return {
    stream,
    release: (releaseOptions = {}) => {
      if (released) return;
      released = true;
      if (!activeSession || activeSession.stream !== stream) return;
      activeSession.refs = Math.max(0, activeSession.refs - 1);
      if (activeSession.refs === 0) {
        if (releaseOptions.immediate) stopActiveStreamNow();
        else startDeferredRelease();
      }
    },
  };
}

export function stopCameraSessionImmediately() {
  stopActiveStreamNow();
}
